import { animationDuration, animationFrameId } from "@/document/animation/applyAnimation";
import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import {
  normalizeAnimationGraph,
  type AnimateAnimationNode,
  type AnimationEndpoint,
  type AnimationGraph,
  type AnimationGraphNode,
  type AnimationGroupNumericOverride,
  type AnimationProperty,
  type AnimationTarget,
  type GroupAnimationNode,
  type MotionAnimationNode,
  type RootAnimationNode,
} from "@/document/model/animation";
import type { OklchColor } from "@/document/model/color";

export interface MermaidAnimationExportOptions {
  document: CurvaDocument;
  rootNodeId?: string;
}

export interface MermaidAnimationExportResult {
  mermaid: string;
  durationMs: number;
  frameName: string;
}

interface MermaidLine {
  from: string;
  to: string;
  label: string;
}

export function exportDocumentAnimationToMermaid(options: MermaidAnimationExportOptions): MermaidAnimationExportResult {
  const graph = normalizeAnimationGraph(options.document.animation);
  if (graph.nodes.length === 0) {
    throw new Error("Animation graph is empty");
  }

  const rootNode = animationRootNode(graph, options.rootNodeId);
  const frameId = animationFrameId(graph, rootNode?.id);
  const frame = frameId ? findSceneNode(options.document, frameId) : undefined;
  const includedNodeIds = activeAnimationNodeIds(graph, rootNode?.id);
  const includedNodes = graph.nodes.filter(node => includedNodeIds.has(node.id));
  const nodeIdToMermaidId = new Map(includedNodes.map(node => [node.id, mermaidId("anim", node.id)]));
  const objectIds = new Set<string>();
  const lines: MermaidLine[] = [];

  for (const connection of graph.connections) {
    if (!includedNodeIds.has(connection.from.nodeId) || !includedNodeIds.has(connection.to.nodeId)) {
      continue;
    }
    lines.push({
      from: nodeIdToMermaidId.get(connection.from.nodeId)!,
      to: nodeIdToMermaidId.get(connection.to.nodeId)!,
      label: connectionLabel(connection.from, connection.to),
    });
  }

  for (const node of includedNodes) {
    collectTargetLines(node, options.document, includedNodeIds, nodeIdToMermaidId, objectIds, lines);
  }

  const header = [
    "%% Curva animation graph",
    `%% Frame: ${frame?.name ?? "Unbound"}`,
    `%% Duration: ${formatNumber(animationDuration(graph, rootNode?.id, options.document))}ms`,
    "flowchart LR",
  ];
  const objectNodes = [...objectIds].sort().map(nodeId => `  ${objectNodeDeclaration(nodeId, options.document)}`);
  const graphNodes = includedNodes.map(node => `  ${animationNodeDeclaration(node, options.document, graph, nodeIdToMermaidId)}`);
  const edges = uniqueLines(lines).map(line => `  ${line.from} -- "${mermaidText(line.label)}" --> ${line.to}`);

  return {
    mermaid: [...header, ...objectNodes, ...graphNodes, ...edges].join("\n") + "\n",
    durationMs: animationDuration(graph, rootNode?.id, options.document),
    frameName: frame?.name || rootNode?.name || "Animation",
  };
}

function animationNodeDeclaration(
  node: AnimationGraphNode,
  document: CurvaDocument,
  graph: AnimationGraph,
  nodeIdToMermaidId: Map<string, string>,
): string {
  return `${nodeIdToMermaidId.get(node.id)!}["${mermaidLabel(animationNodeLabelLines(node, document, graph))}"]`;
}

function objectNodeDeclaration(nodeId: string, document: CurvaDocument): string {
  const node = findSceneNode(document, nodeId);
  return `${mermaidId("obj", nodeId)}(["${mermaidLabel(["Object", node ? `${node.name} (${node.type})` : "Missing object", shortId(nodeId)])}"])`;
}

function animationRootNode(graph: AnimationGraph, rootNodeId: string | undefined): RootAnimationNode | undefined {
  const requested = rootNodeId ? graph.nodes.find((node): node is RootAnimationNode => node.kind === "animation" && node.id === rootNodeId) : undefined;
  return requested ?? graph.nodes.find((node): node is RootAnimationNode => node.kind === "animation");
}

function activeAnimationNodeIds(graph: AnimationGraph, rootNodeId: string | undefined): Set<string> {
  if (!rootNodeId) {
    return new Set(graph.nodes.map(node => node.id));
  }

  const existingIds = new Set(graph.nodes.map(node => node.id));
  const included = new Set<string>(existingIds.has(rootNodeId) ? [rootNodeId] : []);
  const queue = [...included];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const node = graph.nodes.find(candidate => candidate.id === nodeId);
    if (node?.kind === "motion" && node.transition && node.targetAnimationNodeId) {
      enqueue(node.targetAnimationNodeId, existingIds, included, queue);
    }

    for (const connection of graph.connections) {
      if (connection.from.nodeId === nodeId) {
        enqueue(connection.to.nodeId, existingIds, included, queue);
      }
      if (connection.to.nodeId === nodeId) {
        enqueue(connection.from.nodeId, existingIds, included, queue);
      }
    }
  }
  return included;
}

function enqueue(nodeId: string, existingIds: Set<string>, included: Set<string>, queue: string[]): void {
  if (!existingIds.has(nodeId) || included.has(nodeId)) {
    return;
  }
  included.add(nodeId);
  queue.push(nodeId);
}

function collectTargetLines(
  node: AnimationGraphNode,
  document: CurvaDocument,
  includedNodeIds: Set<string>,
  nodeIdToMermaidId: Map<string, string>,
  objectIds: Set<string>,
  lines: MermaidLine[],
): void {
  const to = nodeIdToMermaidId.get(node.id);
  if (!to) {
    return;
  }

  if (node.kind === "group") {
    for (const [index, target] of node.targets.entries()) {
      collectTargetLine(target, document, includedNodeIds, nodeIdToMermaidId, objectIds, lines, to, `target ${index + 1}`);
    }
  } else if (node.kind === "animate") {
    collectTargetLine(node.target, document, includedNodeIds, nodeIdToMermaidId, objectIds, lines, to, "target");
    for (const [index, property] of node.properties.entries()) {
      for (const target of propertyTargets(property)) {
        collectTargetLine(target.target, document, includedNodeIds, nodeIdToMermaidId, objectIds, lines, to, `${property.kind} ${index + 1} ${target.label}`);
      }
    }
  } else if (node.kind === "motion" && node.transition && node.targetAnimationNodeId && includedNodeIds.has(node.targetAnimationNodeId)) {
    const from = nodeIdToMermaidId.get(node.id);
    const target = nodeIdToMermaidId.get(node.targetAnimationNodeId);
    if (from && target) {
      lines.push({ from, to: target, label: "transition target" });
    }
  }
}

function collectTargetLine(
  target: AnimationTarget | undefined,
  document: CurvaDocument,
  includedNodeIds: Set<string>,
  nodeIdToMermaidId: Map<string, string>,
  objectIds: Set<string>,
  lines: MermaidLine[],
  to: string,
  label: string,
): void {
  if (!target) {
    return;
  }

  if (target.kind === "animationNode") {
    const from = includedNodeIds.has(target.nodeId) ? nodeIdToMermaidId.get(target.nodeId) : undefined;
    if (from) {
      lines.push({ from, to, label });
    }
    return;
  }

  objectIds.add(target.nodeId);
  const suffix = target.kind === "sceneNode"
    ? ""
    : target.kind === "vectorVertex"
      ? ` vertex ${shortId(target.vertexId)}`
      : target.kind === "vectorEdge"
        ? ` edge ${shortId(target.edgeId)}`
        : ` ${target.endpoint} handle ${shortId(target.edgeId)}`;
  lines.push({
    from: mermaidId("obj", target.nodeId),
    to,
    label: `${label}${suffix}`,
  });

  // Force missing-object references to stay visible in the diagram.
  findSceneNode(document, target.nodeId);
}

function propertyTargets(property: AnimationProperty): Array<{ label: string; target: AnimationTarget | undefined }> {
  switch (property.kind) {
    case "pin":
      return [{ label: "dependency", target: property.dependencyTarget }];
    case "followPath":
      return [{ label: "path", target: property.pathTarget }];
    case "fitToBBox":
      return [{ label: "box", target: property.target }];
    case "orbit":
      return [{ label: "center", target: property.target }];
    case "lookAt":
      return [{ label: "target", target: property.target }];
    default:
      return [];
  }
}

function animationNodeLabelLines(node: AnimationGraphNode, document: CurvaDocument, graph: AnimationGraph): string[] {
  const base = [`${node.name} (${node.kind})`, shortId(node.id)];
  switch (node.kind) {
    case "animation": {
      const frame = node.frameId ? findSceneNode(document, node.frameId) : undefined;
      return [...base, `frame: ${frame?.name ?? "unbound"}`];
    }
    case "group":
      return groupNodeLabelLines(node, document, base);
    case "animate":
      return animateNodeLabelLines(node, document, base);
    case "motion":
      return motionNodeLabelLines(node, graph, document, base);
  }
}

function groupNodeLabelLines(node: GroupAnimationNode, document: CurvaDocument, base: string[]): string[] {
  return [
    ...base,
    `targets: ${node.targets.length}`,
    `regroup: ${node.regroup}`,
    ...node.targets.slice(0, 4).map((target, index) => `#${index + 1}: ${targetSummary(target, document)}`),
    ...(node.targets.length > 4 ? [`+${node.targets.length - 4} more targets`] : []),
  ];
}

function animateNodeLabelLines(node: AnimateAnimationNode, document: CurvaDocument, base: string[]): string[] {
  return [
    ...base,
    `target: ${targetSummary(node.target, document)}`,
    ...node.properties.map((property, index) => `${index + 1}. ${propertySummary(property)}`),
  ];
}

function motionNodeLabelLines(node: MotionAnimationNode, graph: AnimationGraph, document: CurvaDocument, base: string[]): string[] {
  return [
    ...base,
    `duration: ${formatNumber(node.durationMs)}ms`,
    `offset: ${formatNumber(node.offsetMs)}ms`,
    ...(node.instant ? ["instant: true"] : []),
    ...(node.stutterMs > 0 ? [`stutter: ${formatNumber(node.stutterMs)}ms`] : []),
    ...(node.animateElementsIndependently ? ["children: independent"] : []),
    `easing: ${easingSummary(node.easing)}`,
    ...(node.transition ? [`transition: ${node.transitionEffect ?? "cut"} -> ${transitionTargetSummary(node, graph, document)}`] : []),
  ];
}

function transitionTargetSummary(node: MotionAnimationNode, graph: AnimationGraph, document: CurvaDocument): string {
  const target = node.targetAnimationNodeId ? graph.nodes.find(candidate => candidate.id === node.targetAnimationNodeId) : undefined;
  if (target?.kind !== "animation") {
    return "missing";
  }
  const frame = target.frameId ? findSceneNode(document, target.frameId) : undefined;
  return frame ? `${target.name}/${frame.name}` : target.name;
}

function propertySummary(property: AnimationProperty): string {
  const overrides = groupOverridesSummary(property.groupOverrides);
  const suffix = overrides ? `; overrides: ${overrides}` : "";
  switch (property.kind) {
    case "opacity":
      return `opacity ${formatNumber(property.value * 100)}%${suffix}`;
    case "pin":
      return `pin ${property.anchor} (${formatNumber(property.x)}, ${formatNumber(property.y)})${suffix}`;
    case "followPath":
      return `follow path ${formatNumber(property.progress * 100)}% anchor ${property.anchor}${property.orientToPath ? ", orient to path" : ""}${suffix}`;
    case "strokePath":
      return `stroke path ${formatNumber(property.progress * 100)}%${property.inverse ? ", inverse" : ""}${property.erase ? ", erase" : ""}${suffix}`;
    case "orbit":
      return `orbit ${formatNumber(property.angle)}deg${suffix}`;
    case "lookAt":
      return `look at${suffix}`;
    case "scale":
    case "scaleX":
    case "scaleY":
    case "rotation":
    case "width":
    case "height":
      return `${property.kind} ${formatNumber(property.value)}${suffix}`;
    case "layout":
      return `layout ${property.axis} gap ${formatNumber(property.gap)}${suffix}`;
    case "stack":
    case "inline":
      return `${property.kind} gap ${formatNumber(property.gap)}${suffix}`;
    case "fitToBBox":
      return `fit to bbox pad ${formatNumber(property.pad)}${suffix}`;
    case "prop":
      return `${property.name} ${formatNumber(property.value)}${suffix}`;
    case "fillColor":
      return `fill ${property.fillIndex + 1} ${property.colorSpace} ${property.channels.join(",")} ${colorSummary(property.color)}${suffix}`;
    case "strokeColor":
      return `stroke ${property.strokeIndex + 1} ${property.colorSpace} ${property.channels.join(",")} ${colorSummary(property.color)}${suffix}`;
    case "effectValue":
      return `effect ${property.effectIndex + 1} ${property.field} ${formatNumber(property.value)}${suffix}`;
    case "effectColor":
      return `effect ${property.effectIndex + 1} ${property.colorSpace} ${property.channels.join(",")} ${colorSummary(property.color)}${suffix}`;
  }
}

function groupOverridesSummary(overrides: AnimationProperty["groupOverrides"]): string {
  if (!overrides) {
    return "";
  }
  return Object.entries(overrides)
    .map(([field, override]) => `${field}=${groupOverrideSummary(override)}`)
    .join("; ");
}

function groupOverrideSummary(override: AnimationGroupNumericOverride): string {
  switch (override.kind) {
    case "distributed":
      return `distributed ${formatNumber(override.from)}..${formatNumber(override.to)}`;
    case "random":
      return `random ${formatNumber(override.from)}..${formatNumber(override.to)} seed ${formatNumber(override.seed)}`;
    case "list":
      return `list ${override.values}`;
  }
}

function targetSummary(target: AnimationTarget | undefined, document: CurvaDocument): string {
  if (!target) {
    return "none";
  }
  if (target.kind === "animationNode") {
    return `animation ${shortId(target.nodeId)}`;
  }
  const sceneNode = findSceneNode(document, target.nodeId);
  const label = sceneNode ? `${sceneNode.name} (${sceneNode.type})` : `missing ${shortId(target.nodeId)}`;
  if (target.kind === "sceneNode") {
    return label;
  }
  if (target.kind === "vectorVertex") {
    return `${label} vertex ${shortId(target.vertexId)}`;
  }
  if (target.kind === "vectorEdge") {
    return `${label} edge ${shortId(target.edgeId)}`;
  }
  return `${label} ${target.endpoint} handle ${shortId(target.edgeId)}`;
}

function connectionLabel(from: AnimationEndpoint, to: AnimationEndpoint): string {
  const fromIndex = from.index === undefined ? "" : ` ${from.index + 1}`;
  const toIndex = to.index === undefined ? "" : ` ${to.index + 1}`;
  return `${from.pin}${fromIndex} -> ${to.pin}${toIndex}`;
}

function easingSummary(easing: MotionAnimationNode["easing"]): string {
  if (easing.kind === "preset") {
    return easing.name;
  }
  return `wiggle f${formatNumber(easing.frequency)} p${formatNumber(easing.phase)} a${formatNumber(easing.amplitude)} d${formatNumber(easing.decay)}`;
}

function colorSummary(color: OklchColor): string {
  return `oklch(${formatNumber(color.l)} ${formatNumber(color.c)} ${formatNumber(color.h)} / ${formatNumber(color.alpha)})`;
}

function uniqueLines(lines: MermaidLine[]): MermaidLine[] {
  const seen = new Set<string>();
  return lines.filter(line => {
    const key = `${line.from}\u0000${line.to}\u0000${line.label}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function mermaidId(prefix: string, id: string): string {
  return `${prefix}_${id.replace(/[^a-zA-Z0-9_]/g, "_")}`;
}

function mermaidLabel(lines: string[]): string {
  return lines.map(mermaidText).join("\\n");
}

function mermaidText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, "\\\"")
    .replace(/\r?\n/g, "\\n")
    .replace(/\|/g, "/");
}

function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : Number(value.toFixed(3)).toString();
}
